import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { RefreshCw } from "lucide-react";
import api from "../../services/api";
import { rankDiagnosticCodes } from "../../apis/llm/diagnostics";

// Every deterministic repair the generation pipeline can apply, and every
// validation code it can raise. Listed here rather than derived from the
// response on purpose: a code that has NEVER fired is absent from the API's
// tallies, and absence is exactly the reading worth surfacing — a pass that
// has not fired in three months of real generations is a pass that can go.
const REPAIR_CODES = [
  "repair.liftingTrim",
  "repair.misplacedBlock",
  "repair.leafFlatten",
  "repair.setSchemeLower",
  "repair.doubleEncodedSets",
  "repair.enumeratedSets",
  "repair.wrapperHoist",
  "repair.ghostRest",
  "repair.transitionLike",
  "repair.setRest",
  "repair.missingId",
];

const VALIDATION_CODES = [
  "structure.missing",
  "structure.no_work",
  "structure.leafWithChildren",
  "structure.labelDuration",
  "timing.residual",
  "timing.over",
  "units.invalid",
  "guidance.missing",
  "equipment.unavailable",
  "coverage.blocks",
  "coverage.monotony",
  "setScheme.wrongNode",
  "setScheme.malformed",
  "setScheme.unknownDim",
  "setScheme.lengthMismatch",
];

// Known codes first (ranked by count, zeroes last), then anything the server
// reported that this list doesn't know about — a code left behind by a rename.
const mergeKnown = (counts, known) => {
  const ranked = rankDiagnosticCodes(counts);
  const seen = new Set(ranked.map((row) => row.code));
  const zeroes = known
    .filter((code) => !seen.has(code))
    .map((code) => ({ code, count: 0 }));
  return [...ranked, ...zeroes];
};

const CodeList = ({ title, rows }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {title}
    </p>
    <div className="space-y-1">
      {rows.map(({ code, count }) => (
        <div
          key={code}
          className="flex items-center justify-between gap-2 text-xs"
        >
          <span
            className={`font-mono ${count === 0 ? "text-muted-foreground/60" : ""}`}
          >
            {code}
          </span>
          <Badge
            variant={count === 0 ? "outline" : "secondary"}
            className="text-[10px] shrink-0"
          >
            {count === 0 ? "never" : count}
          </Badge>
        </div>
      ))}
    </div>
  </div>
);

// "14s", "1m 41s". Whole seconds: the spread between models is the point, and
// it is measured in tens of seconds.
export const formatWait = (ms) => {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

// How long each model kept the user waiting, tap to plan (#165).
const SpeedList = ({ rows }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      Speed by model
    </p>
    <div className="space-y-1.5">
      {rows.map((row) => (
        <div key={`${row.provider}/${row.model}`} className="text-xs">
          <p className="font-mono break-all">{row.model || row.provider}</p>
          <p className="tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">
              {formatWait(row.median_ms)}
            </span>
            {" typical · "}
            {formatWait(row.max_ms)}
            {" slowest · "}
            {row.generations}
            {"\u00d7"}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const GenerationDiagnosticsCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getGenerationDiagnostics(90));
    } catch (err) {
      setError(err?.message || "Could not load diagnostics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generations = data?.generations ?? 0;

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">Generation pipeline</CardTitle>
            <CardDescription>
              {generations > 0
                ? `${generations} generations in the last ${data.windowDays} days · ${data.provider_calls} provider calls · ${data.refined} refined, ${data.resolved} cleared`
                : "Which automatic repairs fire, and which checks still catch things"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-xs text-red-500">{error}</p>}
        {!error && generations === 0 && (
          <p className="text-xs text-muted-foreground">
            Nothing recorded yet. Generate a session and the counts appear here.
          </p>
        )}
        {data?.speed?.length > 0 && <SpeedList rows={data.speed} />}
        {generations > 0 && (
          <>
            <CodeList
              title="Repairs applied"
              rows={mergeKnown(data.repairs, REPAIR_CODES)}
            />
            <CodeList
              title="Issues left after repair"
              rows={mergeKnown(data.validation, VALIDATION_CODES)}
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              A pass marked <span className="font-medium">never</span> across a
              few hundred generations is defending against a failure that no
              longer happens — a candidate for deletion rather than
              maintenance.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GenerationDiagnosticsCard;
